import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import cn from 'classnames';
// Types
import {
  GetTipImageState,
  GetTipImageStateVariables,
} from 'api/tips/types/GetTipImageState';
import { TipImageStatus } from 'api/graphql-global-types';
// Api
import { GET_TIP_IMAGE_STATE, GET_TIP } from 'api/tips/queries';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Helpers
import { downloadImageFromUrl } from 'helpers/download';
// Ui3
import Image from 'ui3/Image/Image';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Loader from 'ui3/Loader/Loader';
import Button from 'ui3/Button/Button';
// Styles
import styles from './TipPosterSection.module.scss';

interface TipPosterSectionProps {
  tipId: string;
  imageURL?: string | null;
  className?: string;
}

const TipPosterSection: React.FC<TipPosterSectionProps> = ({
  tipId,
  imageURL,
  className,
}) => {
  const [isImageCompleted, setIsImageCompleted] = useState<boolean>(false);
  const [fetchedImageURL, setFetchedImageURL] = useState<string | null>(null);

  const { data: tipImageState } = useQuery<
    GetTipImageState,
    GetTipImageStateVariables
  >(GET_TIP_IMAGE_STATE, {
    variables: {
      input: {
        id: tipId,
      },
    },
    skip: !tipId || isImageCompleted,
    pollInterval: isImageCompleted ? 0 : 2000,
    onCompleted: (data) => {
      if (data?.getTipImageState?.imageStatus === TipImageStatus.completed) {
        setIsImageCompleted(true);
      }
    },
  });

  const [fetchTipData] = useLazyQuery(GET_TIP, {
    variables: { input: { id: tipId } },
    onCompleted: (data) => {
      const tipImageURL = data?.getTip?.imageURL || data?.tipById?.imageURL;
      if (tipImageURL) {
        setFetchedImageURL(tipImageURL);
      }
    },
  });

  useEffect(() => {
    if (
      tipImageState?.getTipImageState?.imageStatus ===
        TipImageStatus.completed &&
      !isImageCompleted
    ) {
      fetchTipData();
    }

    if (
      tipImageState?.getTipImageState?.imageStatus === TipImageStatus.failed
    ) {
      setIsImageCompleted(true);
      showToast({
        message:
          'Poster generation failed. An automatic retry will follow soon.',
        type: 'error',
        autoClose: 5000,
      });
    }
  }, [
    tipImageState?.getTipImageState?.imageStatus,
    fetchTipData,
    isImageCompleted,
  ]);

  const finalImageURL = fetchedImageURL || imageURL;

  const handleDownloadPoster = () => {
    downloadImageFromUrl(finalImageURL ?? '', `Tip Poster`);
  };

  const handleSharePoster = async () => {
    try {
      await navigator.clipboard.writeText(finalImageURL || '');
      showToast({
        message: 'Copied',
        autoClose: 1000,
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Could not copy text',
        type: 'error',
        autoClose: 1000,
      });
    }
  };
  const showActions = finalImageURL || imageURL;
  return (
    <div className={cn(styles.posterSection, className)}>
      <div
        className={cn(styles.imageContainer, {
          [styles.loading]: !isImageCompleted,
        })}
      >
        {!isImageCompleted && <Loader loading={true} size="xxLarge" />}
        {finalImageURL && (
          <Image
            alt={'Tip Poster'}
            className={styles.posterImage}
            src={finalImageURL}
          />
        )}
      </div>

      <div className={styles.posterButtons}>
        <Button
          className={styles.sharePosterButton}
          variant="primary"
          size="medium"
          disabled={!showActions}
          onClick={handleSharePoster}
        >
          <MillionsIcon name="share" />
          Share
        </Button>
        <Button
          className={styles.downloadPosterButton}
          variant="secondary"
          size="medium"
          disabled={!showActions}
          onClick={handleDownloadPoster}
        >
          <MillionsIcon name="download" />
          Download
        </Button>
      </div>
    </div>
  );
};

export default TipPosterSection;
